/**
 * Enterprise user profile: identity, organization context, security (change password), links.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Form,
  Input,
  Button,
  Space,
  Tabs,
  Descriptions,
  Tag,
  Alert,
  Divider,
  Spin,
  Upload,
  Popconfirm,
  message,
} from 'antd';
import {
  UserOutlined,
  SafetyOutlined,
  BankOutlined,
  SettingOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  IdcardOutlined,
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import authService from '@/services/auth.service';
import companyService from '@/services/company.service';
import userService from '@/services/user.service';
import { resolvePublicAssetUrl } from '@/config/apiBaseUrl';
import { mergeUserClaimsFromToken } from '@/utils/jwtPermissions';
import type { User } from '@/types';
import type { Company } from '@/types';

const { Title, Text, Paragraph } = Typography;

export const UserProfilePage: React.FC = () => {
  const { user: storeUser, setUser } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [imageBusy, setImageBusy] = useState<'avatar' | 'signature' | null>(null);
  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [profileForm] = Form.useForm();
  const [pwForm] = Form.useForm();

  // Do not depend on `storeUser` (whole object): refreshProfile calls setUser(), which updates the
  // store and would recreate this callback → useEffect re-runs → setFieldsValue wipes in-progress typing.
  const sessionUserId = storeUser?.id;
  const refreshProfile = useCallback(async () => {
    setLoading(true);
    try {
      const me = await authService.getCurrentUser();
      const token = authService.getToken();
      const merged = token ? mergeUserClaimsFromToken(me as User, token) : (me as User);
      setProfileUser(merged);
      setUser(merged);
      profileForm.setFieldsValue({
        first_name: merged.first_name,
        last_name: merged.last_name || '',
        phone: merged.phone || '',
      });
      if (merged.company_id) {
        try {
          const c = await companyService.getById(merged.company_id);
          setCompany(c);
        } catch {
          setCompany(null);
        }
      } else {
        setCompany(null);
      }
    } catch {
      const fallback = useAuthStore.getState().user;
      if (fallback?.id) {
        setProfileUser(fallback);
        profileForm.setFieldsValue({
          first_name: fallback.first_name,
          last_name: fallback.last_name || '',
          phone: fallback.phone || '',
        });
        if (fallback.company_id) {
          try {
            const c = await companyService.getById(fallback.company_id);
            setCompany(c);
          } catch {
            setCompany(null);
          }
        } else {
          setCompany(null);
        }
      } else {
        message.error('Could not load your profile. Please sign in again.');
      }
    } finally {
      setLoading(false);
    }
  }, [profileForm, setUser, sessionUserId]);

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  const onSaveProfile = async (values: {
    first_name: string;
    last_name?: string;
    phone?: string;
  }) => {
    setSavingProfile(true);
    try {
      const updated = await authService.updateMyProfile({
        first_name: values.first_name,
        last_name: values.last_name,
        phone: values.phone,
      });
      const token = authService.getToken();
      const merged = token
        ? mergeUserClaimsFromToken({ ...profileUser, ...updated } as User, token)
        : ({ ...profileUser, ...updated } as User);
      setProfileUser(merged);
      setUser(merged);
      message.success('Profile saved');
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const applyUserPatch = (updates: Partial<User>) => {
    const base = profileUser || useAuthStore.getState().user;
    if (!base) return;
    const token = authService.getToken();
    const next = { ...base, ...updates } as User;
    const merged = token ? mergeUserClaimsFromToken(next, token) : next;
    setProfileUser(merged);
    setUser(merged);
  };

  const imageUploadRequest =
    (kind: 'avatar' | 'signature') =>
    async ({ file, onSuccess, onError }: any) => {
      const userId = profileUser?.id || storeUser?.id;
      if (!userId) return;
      setImageBusy(kind);
      try {
        if (kind === 'avatar') {
          const res = await userService.uploadAvatar(userId, file as File);
          applyUserPatch({ avatar_url: res.avatar_url });
          message.success('Profile picture updated');
          onSuccess?.(res, file);
        } else {
          const res = await userService.uploadSignature(userId, file as File);
          applyUserPatch({ signature_image_url: res.signature_image_url });
          message.success('Digital signature updated');
          onSuccess?.(res, file);
        }
      } catch (e: any) {
        message.error(e?.response?.data?.message || 'Upload failed');
        onError?.(e);
      } finally {
        setImageBusy(null);
      }
    };

  const beforeImageUpload = (file: File) => {
    if (!/^image\/(jpeg|png|gif|webp)$/i.test(file.type)) {
      message.error('Only JPEG, PNG, GIF or WebP images are allowed');
      return Upload.LIST_IGNORE;
    }
    if (file.size > 2 * 1024 * 1024) {
      message.error('Image must be 2 MB or smaller');
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const onRemoveImage = async (kind: 'avatar' | 'signature') => {
    const userId = profileUser?.id || storeUser?.id;
    if (!userId) return;
    setImageBusy(kind);
    try {
      if (kind === 'avatar') {
        await userService.removeAvatar(userId);
        applyUserPatch({ avatar_url: undefined });
        message.success('Profile picture removed');
      } else {
        await userService.removeSignature(userId);
        applyUserPatch({ signature_image_url: undefined });
        message.success('Digital signature removed');
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to remove image');
    } finally {
      setImageBusy(null);
    }
  };

  const onChangePassword = async (values: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('New passwords do not match');
      return;
    }
    setChangingPw(true);
    try {
      await authService.changePassword(values.currentPassword, values.newPassword);
      message.success('Password changed successfully');
      pwForm.resetFields();
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.errors?.[0]?.message ||
        e?.message ||
        'Failed to change password';
      message.error(msg);
    } finally {
      setChangingPw(false);
    }
  };

  if (loading && !profileUser) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" tip="Loading profile…" />
      </div>
    );
  }

  const u = profileUser || storeUser;
  if (!u) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message="Not signed in" showIcon />
      </div>
    );
  }

  const displayName =
    `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username;
  const avatarSrc = resolvePublicAssetUrl(u.avatar_url);
  const signatureSrc = resolvePublicAssetUrl(u.signature_image_url);
  const imageAccept = 'image/jpeg,image/png,image/gif,image/webp';

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            <UserOutlined /> My profile
          </Title>
          <Text type="secondary">
            Manage your account details and security. Username and email are managed by your
            administrator.
          </Text>
        </div>

        <Card>
          <Row gutter={[24, 16]} align="middle">
            <Col>
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt={displayName}
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 8,
                    objectFit: 'cover',
                    border: '1px solid #f0f0f0',
                    display: 'block',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #145A54 0%, #096dd9 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 28,
                    fontWeight: 600,
                  }}
                >
                  {(displayName[0] || '?').toUpperCase()}
                </div>
              )}
            </Col>
            <Col flex="1">
              <Title level={4} style={{ margin: 0 }}>
                {displayName}
              </Title>
              <Space wrap size={[8, 8]}>
                <Text type="secondary">
                  <IdcardOutlined /> {u.username}
                </Text>
                {u.is_super_admin && <Tag color="red">Super administrator</Tag>}
                {!u.is_super_admin && u.company_id && (
                  <Tag color="blue">{company?.name || 'Organization user'}</Tag>
                )}
              </Space>
            </Col>
            <Col>
              <Link to="/platform/preferences">
                <Button icon={<SettingOutlined />}>Preferences &amp; display</Button>
              </Link>
            </Col>
          </Row>
        </Card>

        <Tabs
          defaultActiveKey="account"
          items={[
            {
              key: 'account',
              label: (
                <span>
                  <UserOutlined /> Account
                </span>
              ),
              children: (
                <Row gutter={24}>
                  <Col xs={24} lg={14}>
                    <Card title="Contact details" size="small">
                      <Form
                        form={profileForm}
                        layout="vertical"
                        onFinish={onSaveProfile}
                        style={{ maxWidth: 480 }}
                      >
                        <Form.Item
                          name="first_name"
                          label="First name"
                          rules={[{ required: true, message: 'Required' }]}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item name="last_name" label="Last name">
                          <Input />
                        </Form.Item>
                        <Form.Item name="phone" label="Phone">
                          <Input placeholder="Work or mobile" />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={savingProfile}>
                          Save changes
                        </Button>
                      </Form>
                    </Card>
                  </Col>
                  <Col xs={24} lg={10}>
                    <Card title="Identity (read-only)" size="small">
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Username">{u.username}</Descriptions.Item>
                        <Descriptions.Item label="Email">{u.email}</Descriptions.Item>
                        <Descriptions.Item label="User ID">
                          <Text copyable code style={{ fontSize: 12 }}>
                            {u.id}
                          </Text>
                        </Descriptions.Item>
                      </Descriptions>
                      <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
                        To change username or email, contact your platform administrator.
                      </Paragraph>
                    </Card>
                  </Col>
                </Row>
              ),
            },
            {
              key: 'images',
              label: (
                <span>
                  <PictureOutlined /> Photo &amp; signature
                </span>
              ),
              children: (
                <Row gutter={[24, 24]}>
                  <Col xs={24} md={12}>
                    <Card title="Profile picture" size="small">
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div
                          style={{
                            width: 120,
                            height: 120,
                            borderRadius: 8,
                            border: '1px dashed #d9d9d9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            background: '#fafafa',
                          }}
                        >
                          {avatarSrc ? (
                            <img
                              src={avatarSrc}
                              alt="Profile"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <UserOutlined style={{ fontSize: 40, color: '#bfbfbf' }} />
                          )}
                        </div>
                        <Space wrap>
                          <Upload
                            accept={imageAccept}
                            showUploadList={false}
                            beforeUpload={beforeImageUpload}
                            customRequest={imageUploadRequest('avatar')}
                          >
                            <Button
                              icon={<UploadOutlined />}
                              loading={imageBusy === 'avatar'}
                              disabled={imageBusy !== null && imageBusy !== 'avatar'}
                            >
                              {avatarSrc ? 'Change picture' : 'Upload picture'}
                            </Button>
                          </Upload>
                          {avatarSrc && (
                            <Popconfirm
                              title="Remove your profile picture?"
                              okText="Remove"
                              okButtonProps={{ danger: true }}
                              onConfirm={() => onRemoveImage('avatar')}
                            >
                              <Button danger icon={<DeleteOutlined />} disabled={imageBusy !== null}>
                                Remove
                              </Button>
                            </Popconfirm>
                          )}
                        </Space>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Shown in the top bar and collaboration. JPEG, PNG, GIF or WebP, up to 2 MB.
                        </Text>
                      </Space>
                    </Card>
                  </Col>
                  <Col xs={24} md={12}>
                    <Card title="Digital signature" size="small">
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div
                          style={{
                            width: '100%',
                            maxWidth: 320,
                            height: 120,
                            borderRadius: 8,
                            border: '1px dashed #d9d9d9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fff',
                            padding: 8,
                          }}
                        >
                          {signatureSrc ? (
                            <img
                              src={signatureSrc}
                              alt="Signature"
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                          ) : (
                            <Text type="secondary">
                              <EditOutlined /> No signature uploaded
                            </Text>
                          )}
                        </div>
                        <Space wrap>
                          <Upload
                            accept={imageAccept}
                            showUploadList={false}
                            beforeUpload={beforeImageUpload}
                            customRequest={imageUploadRequest('signature')}
                          >
                            <Button
                              icon={<UploadOutlined />}
                              loading={imageBusy === 'signature'}
                              disabled={imageBusy !== null && imageBusy !== 'signature'}
                            >
                              {signatureSrc ? 'Replace signature' : 'Upload signature'}
                            </Button>
                          </Upload>
                          {signatureSrc && (
                            <Popconfirm
                              title="Remove your digital signature?"
                              description="Approved documents printed afterwards will show no signature image for you."
                              okText="Remove"
                              okButtonProps={{ danger: true }}
                              onConfirm={() => onRemoveImage('signature')}
                            >
                              <Button danger icon={<DeleteOutlined />} disabled={imageBusy !== null}>
                                Remove
                              </Button>
                            </Popconfirm>
                          )}
                        </Space>
                        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
                          Printed on documents you approve through the approval workflow (NCR,
                          quotations, purchase orders, etc.). Use a clear scan on a white or
                          transparent background — a PNG with transparency gives the best result.
                          JPEG, PNG, GIF or WebP, up to 2 MB.
                        </Paragraph>
                      </Space>
                    </Card>
                  </Col>
                </Row>
              ),
            },
            {
              key: 'security',
              label: (
                <span>
                  <SafetyOutlined /> Security
                </span>
              ),
              children: (
                <Row gutter={24}>
                  <Col xs={24} md={14}>
                    {u.password_must_change && (
                      <Alert
                        type="warning"
                        showIcon
                        message="Password change required"
                        description="Your administrator requires you to set a new password."
                        style={{ marginBottom: 16 }}
                      />
                    )}
                    <Card title="Change password" size="small">
                      <Paragraph type="secondary">
                        Use a strong password: at least 8 characters, including uppercase,
                        lowercase, a number, and a special character.
                      </Paragraph>
                      <Form
                        form={pwForm}
                        layout="vertical"
                        onFinish={onChangePassword}
                        style={{ maxWidth: 400 }}
                      >
                        <Form.Item
                          name="currentPassword"
                          label="Current password"
                          rules={[{ required: true }]}
                        >
                          <Input.Password autoComplete="current-password" />
                        </Form.Item>
                        <Form.Item
                          name="newPassword"
                          label="New password"
                          rules={[
                            { required: true, message: 'Required' },
                            { min: 8, message: 'At least 8 characters' },
                          ]}
                          hasFeedback
                        >
                          <Input.Password autoComplete="new-password" />
                        </Form.Item>
                        <Form.Item
                          name="confirmPassword"
                          label="Confirm new password"
                          dependencies={['newPassword']}
                          hasFeedback
                          rules={[
                            { required: true },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (!value || getFieldValue('newPassword') === value) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(new Error('Passwords do not match'));
                              },
                            }),
                          ]}
                        >
                          <Input.Password autoComplete="new-password" />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={changingPw}>
                          Update password
                        </Button>
                      </Form>
                    </Card>
                  </Col>
                  <Col xs={24} md={10}>
                    <Card title="Session" size="small">
                      <Space direction="vertical">
                        <Text>
                          <ClockCircleOutlined /> Stay signed in on this device until you log out or
                          the session expires.
                        </Text>
                        <Divider style={{ margin: '8px 0' }} />
                        <Text type="secondary">
                          For account lockouts or access issues, contact your administrator.
                        </Text>
                      </Space>
                    </Card>
                  </Col>
                </Row>
              ),
            },
            {
              key: 'organization',
              label: (
                <span>
                  <BankOutlined /> Organization
                </span>
              ),
              children: (
                <Card size="small">
                  {u.is_super_admin ? (
                    <Alert
                      type="info"
                      showIcon
                      message="Platform administrator"
                      description="You operate across companies. Use Platform → Setup → Company profile to manage tenant details."
                    />
                  ) : (
                    <Descriptions column={1} bordered size="small">
                      <Descriptions.Item label="Company">
                        {company?.name || '—'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Code">{company?.code || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Company ID">
                        {u.company_id ? (
                          <Text copyable code style={{ fontSize: 12 }}>
                            {u.company_id}
                          </Text>
                        ) : (
                          '—'
                        )}
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                  {Array.isArray(u.roles) && u.roles.length > 0 && (
                    <>
                      <Divider orientation="left">
                        <TeamOutlined /> Role codes (from session)
                      </Divider>
                      <Space wrap>
                        {u.roles.map((r) => (
                          <Tag key={r}>{r}</Tag>
                        ))}
                      </Space>
                    </>
                  )}
                </Card>
              ),
            },
          ]}
        />
      </Space>
    </div>
  );
};

export default UserProfilePage;
